import { useMemo } from 'react';

import { useUserState } from 'providers/user-state-provider';

import {
  InventoryComponentFlags,
  InventoryComponentKey,
  NO_INVENTORY_COMPONENTS,
  readInventoryBetaFeedback,
  readInventoryComponents,
} from './inventory-components';
import { ModuleId } from './module-id.enum';

export interface InventoryModuleState {
  /** Whether the vendor has the inventory area at all. */
  enabled: boolean;
  /** Per-component flags. All false when the module is off. */
  components: InventoryComponentFlags;
  /** Whether a given component is usable: module on *and* component on. */
  has: (component: InventoryComponentKey) => boolean;
  /**
   * TEMPORARY (inventory beta): whether the Feedback button shows. False when
   * the module is off.
   */
  betaFeedback: boolean;
}

/**
 * Read the vendor's `INVENTORY` module and its per-component flags.
 *
 * The single place the UI asks "does this shop have inventory, and which parts".
 * Prefer `has(...)` over reading `components` directly — it folds in the
 * module-level switch, so a component can never light up on its own.
 * @returns The module's enabled state and component flags.
 */
export function useInventoryModule(): InventoryModuleState {
  const { isActiveModule, getModuleConfig } = useUserState();

  const enabled = isActiveModule(ModuleId.INVENTORY);
  const settings = getModuleConfig(ModuleId.INVENTORY);

  return useMemo(() => {
    const components = enabled
      ? readInventoryComponents(settings)
      : NO_INVENTORY_COMPONENTS;

    return {
      enabled,
      components,
      has: (component: InventoryComponentKey) => components[component],
      betaFeedback: enabled && readInventoryBetaFeedback(settings),
    };
  }, [enabled, settings]);
}
